import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { messagesApi } from '../lib/api'
import { useAuth } from '../context/AuthContext'

interface LatestMessage {
  id: string
  rentalId: string
  senderName: string
  itemTitle: string
  content: string
}

const DISMISSED_KEY = 'dismissedMessageIds'
const MAX_DISMISSED = 100

function getDismissedIds(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function addDismissedId(id: string) {
  const ids = getDismissedIds()
  if (ids.includes(id)) return
  const next = [...ids, id].slice(-MAX_DISMISSED)
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
  } catch {
    // ignore -- worst case a dismissed toast reappears once
  }
}

// Sibling to NotificationToast.tsx, not a merge of the two: messages
// deliberately don't create a Notification row (see rentalRoutes.ts's
// POST /:id/messages), so they need their own arrival signal
// ('message:new', dispatched by MessageBell.tsx) and their own popup. Same
// stays-until-dismissed / recovers-on-mount / cross-reload dismissal
// behavior as NotificationToast, kept as a separate localStorage key so
// dismissing one type doesn't affect the other.
export default function MessageToast() {
  const [message, setMessage] = useState<LatestMessage | null>(null)
  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    const handleNew = (e: Event) => {
      const detail = (e as CustomEvent<LatestMessage>).detail
      if (detail && !getDismissedIds().includes(detail.id)) setMessage(detail)
    }
    window.addEventListener('message:new', handleNew)
    return () => window.removeEventListener('message:new', handleNew)
  }, [])

  useEffect(() => {
    if (!user) return
    messagesApi.getLatestUnread()
      .then((res) => {
        const latest = res.data.success ? res.data.data : null
        if (latest && !getDismissedIds().includes(latest.id)) setMessage(latest)
      })
      .catch(() => {
        // ignore -- nice-to-have recovery check, not critical path
      })
  }, [user])

  if (!message) return null

  const dismiss = () => {
    addDismissedId(message.id)
    setMessage(null)
  }

  const handleView = () => {
    addDismissedId(message.id)
    setMessage(null)
    navigate(`/rental/${message.rentalId}#messages`)
  }

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        // Stacks below NotificationToast instead of on top of it -- both can
        // legitimately be visible at once (a rental-lifecycle notification and
        // a chat message are independent events), and this is simpler than a
        // shared toast queue for what should be a rare overlap.
        top: 'calc(var(--navbar-height) + 0.75rem + 108px)',
        right: '1rem',
        left: '1rem',
        maxWidth: '380px',
        marginLeft: 'auto',
        zIndex: 1002,
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
        padding: '1rem',
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'flex-start',
      }}
    >
      <span
        className="mdi mdi-message-text-outline"
        style={{ fontSize: '1.25rem', color: 'var(--forest-green)', flexShrink: 0, marginTop: '0.125rem' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '2px' }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
            {message.senderName} — {message.itemTitle}
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            style={{
              flexShrink: 0,
              border: 'none',
              background: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: 1,
              padding: '0.125rem',
              marginTop: '-0.125rem',
              marginRight: '-0.25rem',
            }}
          >
            <span className="mdi mdi-close" />
          </button>
        </div>
        <div
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-secondary)',
            marginBottom: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {message.content}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={handleView}
            style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--forest-green)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            View
          </button>
          <button
            type="button"
            onClick={dismiss}
            style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}
